<template>
    <div>
        <div class="h6">Student Edition</div>
        <div v-if="uploading_student_edition">
            <b-spinner small label="Loading..." />
        </div>
        <div v-else>
            <div v-if="unit_data.student_edition_url">
                <b-list-group>
                    <b-list-group-item>
                        <div class="d-flex align-items-center">
                            <div class="d-flex flex-column">
                                <b-link
                                    :href="`${CDN_URL}${unit_data.student_edition_url}`"
                                    target="_blank"
                                    class="text-primary"
                                >
                                    {{ unit_data.student_edition_title }} - SWO Link (will not immediately update)
                                </b-link>
                                <b-link
                                    :href="`${AWS_URL}${unit_data.student_edition_url}`"
                                    target="_blank"
                                    class="text-primary"
                                >
                                    {{ unit_data.student_edition_title }} - SBP Link
                                </b-link>
                            </div>
                            <div v-if="!disabled" class="ml-auto actions">
                                <b-link
                                    href="#" class="text-danger"
                                    @click.prevent="removeStudentEdition"
                                    v-if="can_update_week"
                                >
                                    <font-awesome-icon :icon="['fad', 'trash']" />
                                </b-link>
                            </div>
                        </div>
                    </b-list-group-item>
                </b-list-group>
            </div>

            <div v-else>
                <b-form-file
                    v-model="student_edition_file"
                    accept="application/pdf"
                    :disabled="!can_update_week || disabled"
                />
                <b-form-text>
                    <span class="text-danger">No file has been uploaded</span>
                </b-form-text>
            </div>
        </div>
    </div>
</template>

<script>
import {mapState} from 'vuex'
import { AWS_URL, CDN_URL } from '@/common/urlHelpers'

export default {
    data: () => ({
        uploading_student_edition: false,
        student_edition_file: null,
    }),
    props: {
        'unit': Object,
        'unit_data': Object,
        'units_are_lessons': Boolean,
        'disabled': Boolean,
    },
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        can_update_week() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_week')
        },
        can_create_flipbook() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'create_flipbook')
        },
        can_delete_flipbook() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'delete_flipbook')
        },
        AWS_URL() {
            return AWS_URL
        },
        CDN_URL() {
            return CDN_URL
        },
    },
    watch: {
        'student_edition_file': {
            handler(file) {
                if (file) {
                    this.uploadStudentEdition(file)
                }
            },
        },
    },
    methods: {
        async uploadStudentEdition(file) {
            if (!file) {
                return
            }
            try {
                this.uploading_student_edition = true
                const form_data = new FormData()
                form_data.append('student_edition', file)
                form_data.append('are_lessons', this.units_are_lessons)

                const response = await this.axios.post(`/api/units/add_student_edition/${this.unit.id}`, form_data)
                if (!response.data || !response.data.student_edition_url) {
                    throw new Error('Student Edition URL not returned')
                }
                this.$set(this.unit, 'student_edition_url', response.data.student_edition_url)
                this.$set(this.unit_data, 'student_edition_url', response.data.student_edition_url)
                this.$set(this.unit, 'student_edition_title', response.data.student_edition_title)
                this.$set(this.unit_data, 'student_edition_title', response.data.student_edition_title)

                await this.handleFlipBookUpdate()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to upload Student Edition: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.student_edition_file = null
                this.uploading_student_edition = false
            }
        },
        async handleFlipBookUpdate() {
            const flipbooks = await this.axios.get(`/api/units/flipbooks/${this.unit.id}`)
            if (!flipbooks || !flipbooks.data) {
                return
            }
            const seFlipBook = flipbooks.data.find((book) => book.book_type === 'student')
            if (!seFlipBook) {
                return
            }
            if (!this.can_create_flipbook || !this.can_delete_flipbook) {
                await this.$swal({
                    html: 'This Student Edition has an existing Flip Book that will need to be updated.',
                })
                return
            }

            const result = await this.$swal({
                html: 'This Student Edition has an existing Flip Book. Update it?',
                showCancelButton: true,
                confirmButtonText: 'Update',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#3498db',
                reverseButtons: true,
            })
            if (result.value) {
                const payload = {
                    'unit_id': this.unit.id,
                    'type': 'student',
                    'url': this.unit_data.student_edition_url,
                    'title': `Student Edition${this.unit.order_number ? ' ' + this.unit.order_number : ''}`,
                }
                await this.axios.delete(`/api/flipbook/${seFlipBook.id}`)
                await this.axios.post('/api/flipbook', payload)
            }

        },
        async removeStudentEdition() {
            const label = this.unit.title ? `<strong>${this.unit.title}</strong>` : 'this week'
            const message = `Are you sure you want to remove the Student Edition for ${label}?`

            const result = await this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Remove Student Edition',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
            })
            if (!result.value) {
                return
            }

            try {
                await this.axios.post(`/api/units/remove_student_edition/${this.unit.id}`)
                this.$set(this.unit_data, 'student_edition_url', null)
                this.$set(this.unit_data, 'student_edition_title', null)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to remove Student Edition: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
    }
}
</script>
